import { useCallback, useEffect, useRef, useState } from 'react';

import { useApi } from '../../../shared/api/api-context.js';
import { useTranslation } from '../../../shared/use-translation.js';

/**
 * Google Identity Services credential response.
 */
interface CredentialResponse {
  readonly credential: string;
}

interface GoogleAccounts {
  readonly id: {
    initialize(config: {
      readonly client_id: string;
      readonly callback: (response: CredentialResponse) => void;
    }): void;
    readonly prompt: (notification?: (notification: unknown) => void) => void;
    readonly renderButton: (
      parent: HTMLElement,
      options: {
        readonly theme?: 'outline' | 'filled_blue' | 'filled_black';
        readonly size?: 'large' | 'medium' | 'small';
        readonly text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
        readonly shape?: 'rectangular' | 'pill' | 'circle' | 'square';
        readonly width?: number;
        readonly locale?: string;
      },
    ) => void;
  };
}

declare global {
  interface Window {
    google?: { readonly accounts: GoogleAccounts };
  }
}

const SCRIPT_ID = 'google-gsi-script';
const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const BUTTON_WIDTH = 280;

interface ProviderConfig {
  readonly googleClientId?: string | undefined;
}

function loadGoogleScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing !== null) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      resolve();
    };
    script.onerror = () => {
      reject(new Error('Failed to load Google Identity Services.'));
    };
    document.head.appendChild(script);
  });
}

interface ProviderButtonsProps {
  readonly onGoogleCredential: (credential: string) => void;
  readonly disabled?: boolean;
}

export function ProviderButtons({
  onGoogleCredential,
  disabled = false,
}: ProviderButtonsProps): React.JSX.Element {
  const { t } = useTranslation();
  const api = useApi();
  const containerRef = useRef<HTMLDivElement>(null);
  const [scriptReady, setScriptReady] = useState(false);
  const [clientId, setClientId] = useState<string | undefined>(undefined);

  const handleCredential = useCallback(
    (response: CredentialResponse) => {
      if (typeof response.credential === 'string' && response.credential.length > 0) {
        onGoogleCredential(response.credential);
      }
    },
    [onGoogleCredential],
  );

  useEffect(() => {
    let cancelled = false;
    void api
      .get<ProviderConfig>('/v1/auth/guest/providers')
      .then((response) => {
        if (!cancelled) setClientId(response.googleClientId);
      })
      .catch(() => {
        if (!cancelled) setClientId(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  useEffect(() => {
    let cancelled = false;
    void loadGoogleScript()
      .then(() => {
        if (!cancelled) setScriptReady(true);
      })
      .catch(() => {
        if (!cancelled) setScriptReady(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (clientId === undefined || !scriptReady) return;
    const accounts = window.google?.accounts;
    if (accounts === undefined || containerRef.current === null) return;
    accounts.id.initialize({ client_id: clientId, callback: handleCredential });
    accounts.id.renderButton(containerRef.current, {
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
      width: BUTTON_WIDTH,
    });
  }, [clientId, scriptReady, handleCredential]);

  return (
    <div className="space-y-3">
      <p className="text-center text-sm text-muted-foreground">{t('auth.orContinueWith')}</p>
      <div className="flex justify-center">
        <div
          ref={containerRef}
          className={disabled ? 'pointer-events-none opacity-50' : ''}
          aria-disabled={disabled}
        />
      </div>
    </div>
  );
}
